import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, ArrowLeft, Sparkles, UserPlus } from 'lucide-react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { SERVICE_OPTIONS, DEFAULT_PAYMENT_TERMS_DAYS } from '../lib/services';

const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const InvoiceUpload = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [file, setFile] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    invoice_no: '',
    date: today,
    due_date: addDays(today, DEFAULT_PAYMENT_TERMS_DAYS),
    company_id: '',
    client_id: '',
    service: '',
    amount: '',
    gst_percent: 18,
    tds_percent: 0,
    status: 'pending',
    notes: '',
  });
  const [serviceMode, setServiceMode] = useState('preset');
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanInfo, setScanInfo] = useState('');
  const [unmatchedClient, setUnmatchedClient] = useState(null); // { name, company_id? } when AI extracted a client we don't have
  const [creatingClient, setCreatingClient] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data));
    api.get('/clients').then(r => setClients(r.data));
  }, []);

  const handleFile = (f) => {
    setFile(f);
    setScanInfo('');
    setUnmatchedClient(null);
  };

  const createClientFromScan = async () => {
    if (!unmatchedClient?.name) return;
    setCreatingClient(true);
    try {
      const payload = {
        name: unmatchedClient.name,
        client_type: 'Other',
        client_status: 'active',
        company_id: unmatchedClient.company_id || form.company_id || (companies[0] && companies[0].id),
      };
      if (!payload.company_id) { alert('Please pick a Company first.'); setCreatingClient(false); return; }
      const res = await api.post('/clients', payload);
      const newClient = res.data;
      setClients(prev => [...prev, newClient]);
      setForm(prev => ({ ...prev, client_id: newClient.id, company_id: newClient.company_id }));
      setUnmatchedClient(null);
      setScanInfo(prev => `${prev} · ✓ Created client "${newClient.name}"`);
    } catch (e2) {
      alert('Could not create client: ' + (e2.response?.data?.detail || e2.message));
    } finally {
      setCreatingClient(false);
    }
  };

  const runAIScan = async () => {
    if (!file) { setErr('Pick a file first'); return; }
    setScanning(true); setErr(''); setScanInfo('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/invoices/scan', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 });
      const d = res.data.extracted || {};

      // Best-effort fuzzy match against existing clients & companies
      const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const matchEntity = (list, label) => {
        if (!label) return '';
        const n = normalize(label);
        const exact = list.find(x => normalize(x.name) === n || normalize(x.full_name || '') === n);
        if (exact) return exact.id;
        const partial = list.find(x => n && (normalize(x.name).includes(n) || n.includes(normalize(x.name))));
        return partial ? partial.id : '';
      };

      const matchedCompany = matchEntity(companies, d.sender_name);
      const filteredCs = matchedCompany ? clients.filter(c => c.company_id === matchedCompany) : clients;
      const matchedClient = matchEntity(filteredCs, d.client_name);

      setForm(prev => ({
        ...prev,
        invoice_no: d.invoice_no || prev.invoice_no,
        date: d.date || prev.date,
        due_date: d.due_date || prev.due_date,
        company_id: matchedCompany || prev.company_id,
        client_id: matchedClient || prev.client_id,
        service: d.service || prev.service,
        amount: d.total != null ? String(d.total) : (d.subtotal != null ? String(d.subtotal) : prev.amount),
        notes: d.notes || prev.notes,
      }));
      if (d.service && !SERVICE_OPTIONS.includes(d.service)) setServiceMode('custom');

      const lines = [`Extracted ${d.invoice_no || '?'}, ${d.client_name || '?'}, ₹${d.total || d.subtotal || '?'}`];
      if (!matchedCompany && d.sender_name) lines.push(`⚠ Sender "${d.sender_name}" — no match, please pick manually.`);
      if (!matchedClient && d.client_name) {
        lines.push(`⚠ Client "${d.client_name}" — no match.`);
        setUnmatchedClient({ name: d.client_name, company_id: matchedCompany || '' });
      } else {
        setUnmatchedClient(null);
      }
      setScanInfo(lines.join(' · '));
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || 'AI scan failed');
    } finally {
      setScanning(false);
    }
  };

  // Auto-suggest next invoice number when company changes
  useEffect(() => {
    if (form.company_id) {
      api.get(`/invoices/next-number/${form.company_id}`).then(r => setForm(f => ({ ...f, invoice_no: r.data.next_number })));
    }
  }, [form.company_id]);

  const filteredClients = form.company_id ? clients.filter(c => c.company_id === form.company_id) : clients;

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr('Please attach the invoice PDF'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const res = await api.post('/invoices/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      navigate(`/invoices/${res.data.id}`);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm w-full transition-colors";
  const labelCls = "block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2";

  return (
    <Layout title="Upload Invoice">
      <div className="max-w-3xl">
        <button onClick={() => navigate('/invoices')} className="flex items-center font-body text-sm text-text-secondary hover:text-maroon mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> Back to Invoices
        </button>
        <div className="bg-surface border border-border p-8 fade-up">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon mb-1">— External Invoice</p>
          <h2 className="font-heading text-2xl font-medium text-text-primary mb-2">Upload a PDF Invoice</h2>
          <p className="font-body text-sm text-text-secondary mb-6">
            Use this when the invoice was created elsewhere (e.g. by another team member). The PDF is stored and linked to a normal invoice record so it shows up in collections, MRR, and reports.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className={labelCls}>Invoice PDF</label>
              <label data-testid="upload-pdf-dropzone" className="flex flex-col items-center justify-center border-2 border-dashed border-border hover:border-maroon p-8 cursor-pointer transition-colors">
                <Upload className="w-6 h-6 text-text-secondary mb-2" strokeWidth={1.5} />
                <p className="font-body text-sm text-text-primary">{file ? file.name : 'Click to choose a PDF (or drop here)'}</p>
                <input data-testid="upload-pdf-input" type="file" accept="application/pdf,image/*" onChange={(e) => handleFile(e.target.files[0])} className="hidden" />
              </label>
              {file && (
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <button data-testid="ai-scan-button" type="button" onClick={runAIScan} disabled={scanning} className="bg-transparent text-maroon border border-maroon hover:bg-cream px-4 py-2 font-heading text-sm flex items-center transition-colors disabled:opacity-50">
                    <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} /> {scanning ? 'Scanning…' : 'Scan with AI & Auto-fill'}
                  </button>
                  {scanInfo && <p data-testid="ai-scan-info" className="font-body text-xs text-text-secondary flex-1 min-w-0">{scanInfo}</p>}
                </div>
              )}
              {unmatchedClient?.name && (
                <div data-testid="unmatched-client-banner" className="mt-3 flex items-center gap-3 p-3 bg-cream border border-maroon/30">
                  <UserPlus className="w-4 h-4 text-maroon flex-shrink-0" strokeWidth={1.5} />
                  <p className="font-body text-xs text-text-primary flex-1">
                    The client <strong>&ldquo;{unmatchedClient.name}&rdquo;</strong> doesn&apos;t exist yet. Create it now and use it on this invoice?
                  </p>
                  <button
                    data-testid="create-client-from-scan"
                    type="button"
                    onClick={createClientFromScan}
                    disabled={creatingClient}
                    className="bg-maroon text-white hover:bg-maroon-hover px-3 py-1.5 font-heading text-xs flex items-center transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> {creatingClient ? 'Creating…' : `Create "${unmatchedClient.name.slice(0, 22)}${unmatchedClient.name.length > 22 ? '…' : ''}"`}
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Company</label>
                <select data-testid="upload-company" required value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value, client_id: '' })} className={inputCls}>
                  <option value="">Pick company...</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.full_name || c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Client</label>
                <select data-testid="upload-client" required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className={inputCls}>
                  <option value="">Pick client...</option>
                  {filteredClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Invoice Number</label>
                <input data-testid="upload-invoice-no" required value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Amount (subtotal, ₹)</label>
                <input data-testid="upload-amount" required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>GST %</label>
                <input data-testid="upload-gst" type="number" step="0.01" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>TDS % <span className="text-text-secondary lowercase normal-case tracking-normal">(optional)</span></label>
                <input data-testid="upload-tds" type="number" step="0.01" value={form.tds_percent} onChange={(e) => setForm({ ...form, tds_percent: e.target.value })} className={inputCls} placeholder="0 to skip" />
              </div>
              <div>
                <label className={labelCls}>Date</label>
                <input data-testid="upload-date" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, due_date: addDays(e.target.value, DEFAULT_PAYMENT_TERMS_DAYS) })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Due Date</label>
                <input data-testid="upload-due-date" required type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Service</label>
                <select
                  data-testid="upload-service"
                  value={serviceMode === 'custom' ? '__other__' : form.service}
                  onChange={(e) => {
                    if (e.target.value === '__other__') {
                      setServiceMode('custom');
                      setForm({ ...form, service: '' });
                    } else {
                      setServiceMode('preset');
                      setForm({ ...form, service: e.target.value });
                    }
                  }}
                  className={inputCls}
                >
                  <option value="">Select service</option>
                  {SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  <option value="__other__">Other…</option>
                </select>
                {serviceMode === 'custom' && (
                  <input data-testid="upload-service-custom" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className={`${inputCls} mt-2`} placeholder="Custom service name" />
                )}
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select data-testid="upload-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                  <option>pending</option><option>sent</option><option>paid</option><option>partial</option><option>overdue</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>Notes (optional)</label>
              <textarea data-testid="upload-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
            </div>

            {err && <p className="font-body text-sm text-maroon">{err}</p>}

            <div className="flex justify-end pt-4 border-t border-border">
              <button data-testid="upload-submit" type="submit" disabled={busy} className="bg-maroon text-white hover:bg-maroon-hover px-6 py-2.5 font-heading text-sm transition-colors disabled:opacity-50 flex items-center">
                <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} /> {busy ? 'Uploading…' : 'Upload Invoice'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default InvoiceUpload;
